import React from "react";
import {
  Alert,
  AlertDescription,
  Box,
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
  Field,
  FieldLabel,
  Icon,
  Input,
  Text,
  toastManager,
} from "@salestrak/ui";

import type { IconName } from "@salestrak/ui";
import { useAuth } from "#app/lib/auth-context";
import * as AuthService from "#app/lib/auth-service";
import { SettingsGroup, SettingsGroupRow } from "#app/components/settings/settings-group";

// The auth service's source set is open (no codegen for its REST API), so
// this maps the providers this build knows how to present; unknown ones are
// skipped rather than rendered with a wrong label or icon.
const SOURCE_META: Record<string, { label: string; icon: IconName }> = {
  password: { label: "Password", icon: "lock" },
  google: { label: "Google", icon: "google" },
};

const FORM_ID = "change-password-form";

/**
 * The profile page's Security group: which sign-in methods are connected to
 * the signed-in account (from the auth service, not workspace data — so this
 * only ever renders for self), and a change-password dialog when a password
 * credential exists. Renders nothing until the sources load; a load failure
 * shows a retry instead of hiding, because this group holds the only
 * change-password affordance in the app and a silent hide reads as "password
 * changes don't exist here".
 */
export function SecuritySettings() {
  const { getAccessToken } = useAuth();
  const [sources, setSources] = React.useState<string[] | null>(null);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [attempt, setAttempt] = React.useState(0);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const token = await getAccessToken();
        // A momentarily null token is a failed load like any other — the group
        // must not go quiet over it.
        if (!token) throw new Error("Not authenticated");
        const loaded = await AuthService.fetchAuthSources(token);
        if (!cancelled) {
          setSources(loaded);
          setLoadFailed(false);
        }
      } catch {
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAccessToken, attempt]);

  if (loadFailed) {
    return (
      <SettingsGroup label="Security">
        <SettingsGroupRow
          label="Connected accounts"
          subtitle="We couldn't load your sign-in methods."
        >
          <Box direction="row" justify="end">
            <Button
              variant="ghost"
              loading={loading}
              onClick={() => setAttempt((n) => n + 1)}
            >
              Try again
            </Button>
          </Box>
        </SettingsGroupRow>
      </SettingsGroup>
    );
  }

  if (!sources || sources.length === 0) return null;
  const hasPassword = sources.includes("password");

  return (
    <SettingsGroup label="Security">
      <SettingsGroupRow
        label="Connected accounts"
        subtitle="Ways you can sign in to SalesTrak"
      >
        <Box direction="row" justify="end" align="center" gap="lg">
          {sources.map((source) => {
            const meta = SOURCE_META[source];
            if (!meta) return null;
            return (
              <Box key={source} direction="row" align="center" gap="xs">
                <Icon name={meta.icon} size="md" />
                <Text size="sm">{meta.label}</Text>
              </Box>
            );
          })}
        </Box>
      </SettingsGroupRow>

      <SettingsGroupRow
        label="Password"
        subtitle={hasPassword ? undefined : "We'll email you a link to create one"}
      >
        <Box direction="row" justify="end">
          {hasPassword ? (
            <Button variant="ghost" onClick={() => setDialogOpen(true)}>
              Change password...
            </Button>
          ) : (
            <SetPasswordButton />
          )}
        </Box>
      </SettingsGroupRow>
      {hasPassword ? (
        <ChangePasswordDialog open={dialogOpen} onOpenChange={setDialogOpen} />
      ) : null}
    </SettingsGroup>
  );
}

/**
 * "Set password" for accounts with no password credential (e.g. Google
 * sign-ups). Sends the emailed set-password link — identity comes from the
 * session, so there is nothing to type. One send per page view; the settled
 * "Email sent" state stops accidental re-mails.
 */
function SetPasswordButton() {
  const { getAccessToken } = useAuth();
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  const handleSend = async () => {
    setSending(true);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error("Not authenticated");
      await AuthService.requestPasswordSetupEmail(token);
      setSent(true);
      toastManager.add({ title: "Password email sent" });
    } catch {
      toastManager.add({ title: "Failed to send password email", type: "error" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Button
      variant="ghost"
      loading={sending}
      disabled={sent}
      onClick={() => void handleSend()}
    >
      {sent ? "Email sent" : "Set password..."}
    </Button>
  );
}

function ChangePasswordDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { getAccessToken } = useAuth();
  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  // A reopened dialog starts clean — never with a half-typed password.
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setError("");
    }
    onOpenChange(next);
  };

  const handleSave = async () => {
    setError("");
    if (newPassword.length < AuthService.PASSWORD_MIN_LENGTH) {
      setError(
        `New password must be at least ${AuthService.PASSWORD_MIN_LENGTH} characters.`
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error("Not authenticated");
      await AuthService.changePassword(token, currentPassword, newPassword);
      toastManager.add({ title: "Password changed" });
      handleOpenChange(false);
    } catch (err: any) {
      setError(err.message ?? "Failed to change password. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void handleSave();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPopup>
        <DialogHeader>
          <DialogTitle>Change password</DialogTitle>
        </DialogHeader>
        <Box
          as="form"
          id={FORM_ID}
          direction="column"
          gap="md"
          paddingBottom="lg"
          paddingX="lg"
          onSubmit={handleSubmit}
        >
          <Field>
            <FieldLabel>Current password</FieldLabel>
            <Input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoFocus
              autoComplete="current-password"
              disabled={saving}
            />
          </Field>
          <Field>
            <FieldLabel>New password</FieldLabel>
            <Input
              type="password"
              placeholder={`At least ${AuthService.PASSWORD_MIN_LENGTH} characters`}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              disabled={saving}
            />
          </Field>
          <Field>
            <FieldLabel>Confirm new password</FieldLabel>
            <Input
              type="password"
              placeholder="Re-enter your new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              disabled={saving}
            />
          </Field>

          {error ? (
            <Alert variant="error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
        </Box>
        {/* The footer's primary action submits the form element above via the
            form attribute, so Enter in any field and the button share one
            submit path. */}
        <DialogFooter>
          <Button
            disabled={saving}
            onClick={() => handleOpenChange(false)}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            disabled={
              saving ||
              !currentPassword.trim() ||
              !newPassword ||
              !confirmPassword.trim()
            }
            loading={saving}
          >
            Change password
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
